import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { createNuwweImages } from "../api";
import { UploadImagesS3 } from "@coninsa-ui/upload-file";

import Alert from "../components/Alert";
import * as Modal from "../components/Modal";
import { Button, toast } from "@coninsa/core";

import cleaningIconUrl from "../../assets/icons/cleaning.svg";
import lightingIconUrl from "../../assets/icons/lighting.svg";
import broadnessIconUrl from "../../assets/icons/broadness.svg";
import { queryString } from "@coninsa/utils";

type T_props = {
  onSuccess: () => void;
  onBack: () => void;
};

type T_fields = {
  images: Array<{ url: string; name: string }>;
  without_pictures: boolean;
};

function Step4({ onSuccess, onBack }: T_props) {
  const [showModal, setShowModal] = useState(false);
  const {
    control,
    formState: { isSubmitting, errors },
    handleSubmit,
    register,
    setError,
    watch,
  } = useForm<T_fields>({
    defaultValues: async () => {
      const values = JSON.parse(
        localStorage.getItem("consignation.images") ?? "{}"
      );

      return values;
    },
  });

  const withoutPictures = watch("without_pictures");

  const onSubmit = async (data: T_fields) => {
    localStorage.setItem("consignation.images", JSON.stringify(data));

    if (withoutPictures) {
      onSuccess();
      return;
    }

    const code = queryString.get("property");

    if (code) {
      try {
        if (data.images.length < 3) {
          setError("images", { message: "El minimo de imagenes debe ser 3." });
        } else {
          const body = data.images.map((image) => image.url);
          const response = await createNuwweImages(code, body);
          toast.success({ message: "Las imágenes fueron adicionados." });
          onSuccess();
        }
      } catch (error) {
        toast.error({ message: "Algo salió mal." });
      }
    } else {
      toast.error({ message: "El código de propiedad no está definido." });
    }
  };

  const handleClose = () => {
    setShowModal(false);
  };

  return (
    <div>
      <div className="co-card max-w-4xl mx-auto">
        <h3 className="font-bold mb-4">Sube tus fotografías</h3>
        <Alert className="is-orange">
          <span className="is-highlight">
            ¡No olvides subir las fotos de tu inmueble!
          </span>
          <p>
            Así tendrás el 98% más de probabilidad de arrrendar tu inmueble.
            ¡Anímate a subirlas para continuar con el proceso!
          </p>
        </Alert>
        <Alert className="is-primary">
          <span className="is-highlight">
            ¿No sabes cómo tomar las fotografías?
          </span>
          <p>
            Revisa nuestros{" "}
            <a
              className="co-link is-secondary"
              href="#"
              onClick={() => setShowModal(true)}
            >
              consejos
            </a>{" "}
            para que tus fotografías sean geniales y logres tener una mejor
            impresión.
          </p>
        </Alert>

        <form className="co-form gap-y-8" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-control is-dropzone">
            <p className="help-text mb-2">
              El peso máximo de las imágenes no pude superar los 500 KB.
            </p>

            <UploadImagesS3
              name="images"
              control={control}
              accept={{ 'image/*': ['.*'], }}
              max={10}
            />

            {errors.images && (
              <p className="form-error">{errors.images.message}</p>
            )}
          </div>

          <div className="form-control is-checkbox bg-gray-100 p-3">
            <input
              type="checkbox"
              id="without_pictures"
              {...register("without_pictures")}
            />
            <label htmlFor="without_pictures" className="data-policy">
              No tengo fotos para subir en este momento. Requiero del
              acompañamiento de un asesor.
            </label>
          </div>

          <div className="flex justify-between gap-2 md:flex-row">
            <Button
              type="button"
              onClick={onBack}
              className="co-button is-secondary is-large has-fixed-icon"
            >
              <i className="co-icon icon-arrow"></i>
              Regresar
            </Button>

            <Button
              loading={isSubmitting}
              className="co-button is-progress-default is-large w-full md:w-auto"
            >
              Continuar
            </Button>
          </div>
        </form>
      </div>

      <Modal.Root isOpen={showModal}>
        <Modal.Body>
          <h3 className="co-heading-3 is-primary mb-8">
            Consejos para tomar mejores fotografías
          </h3>
          <ul>
            <li className="featured-list">
              <img src={cleaningIconUrl} className="shadow-lg rounded-full" />
              <div className="content">
                <h4>Limpieza y orden</h4>
                <p>
                  Antes de capturar las imágenes, asegúrate de que tu propiedad
                  esté impecable, libre de objetos que distraigan al usuario
                  como ropa, cajas o alimentos.
                </p>
              </div>
            </li>

            <li className="featured-list">
              <img src={lightingIconUrl} className="shadow-lg rounded-full" />
              <div className="content">
                <h4>Mientras más luz, mejor</h4>
                <p>
                  Procura tomar fotografías de tus espacios durante la luz del
                  día, preferiblemente en una jornada soleada. Esto garantizará
                  que tus publicaciones luzcan más atractivas y nítidas para los
                  usuarios.
                </p>
              </div>
            </li>

            <li className="featured-list">
              <img src={broadnessIconUrl} className="shadow-lg rounded-full" />
              <div className="content">
                <h4>Incluye más espacio</h4>
                <p>
                  Con el fin de obtener una toma de calidad, coloca la cámara en
                  la intersección de las esquinas de las paredes, orientada
                  hacia el fondo del espacio.
                </p>
              </div>
            </li>
          </ul>
          <div className="flex flex-wrap justify-end">
            <button
              onClick={handleClose}
              className="co-button is-secondary flex items-center gap-4 text-base"
            >
              CERRAR VENTANA
            </button>
          </div>
        </Modal.Body>
      </Modal.Root>
    </div>
  );
}

export default Step4;
